import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Newspaper, Calendar, ArrowRight, Clock, ArrowLeft, Sparkles,
  MessageSquare, Share2, ChevronRight, Search, Heart, Send, LogIn, X,
  Trash2, TrendingUp, Flame, Filter, ArrowUpRight, Bookmark, BookmarkCheck,
  Link2, Radio, Eye, ThumbsUp, Reply, CornerDownRight, Users
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { getImageUrl } from '../utils/imageUtils';
import { toast } from 'sonner';
import axios from 'axios';
import UserHoverCard from '../components/UserHoverCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Author-tag styling — used both in comments and elsewhere
const TAG_COLORS = {
  Executive:            { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/30' },
  Staff:                { bg: 'bg-[#00E5FF]/15', text: 'text-[#00E5FF]', border: 'border-[#00E5FF]/30' },
  VIP:                  { bg: 'bg-purple-500/15', text: 'text-purple-300', border: 'border-purple-500/30' },
  Moderator:            { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  'Community Champion': { bg: 'bg-pink-500/15', text: 'text-pink-300', border: 'border-pink-500/30' },
  Verified:             { bg: 'bg-blue-500/15', text: 'text-blue-300', border: 'border-blue-500/30' },
  'OG Member':          { bg: 'bg-orange-500/15', text: 'text-orange-300', border: 'border-orange-500/30' },
  'Top Contributor':    { bg: 'bg-rose-500/15', text: 'text-rose-300', border: 'border-rose-500/30' },
  default:              { bg: 'bg-white/[0.06]', text: 'text-white/60', border: 'border-white/10' },
};

// Deterministic gradient chosen by username string — makes each commenter feel unique
const AVATAR_GRADIENTS = [
  'from-[#00E5FF] to-[#8A2BE2]',
  'from-fuchsia-500 to-rose-500',
  'from-amber-400 to-orange-500',
  'from-emerald-400 to-cyan-500',
  'from-indigo-500 to-purple-500',
  'from-pink-500 to-red-500',
  'from-sky-400 to-blue-600',
  'from-yellow-400 to-amber-600',
];
const gradientFor = (str = '') => {
  let sum = 0;
  for (let i = 0; i < str.length; i++) sum += str.charCodeAt(i);
  return AVATAR_GRADIENTS[sum % AVATAR_GRADIENTS.length];
};

// ─── UI Helpers ──────────────────────────────────────────
const UserTag = ({ tag }) => {
  const c = TAG_COLORS[tag] || TAG_COLORS.default;
  return (
    <span
      data-testid={`user-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.08em] ${c.bg} ${c.text} border ${c.border}`}>
      {tag}
    </span>
  );
};

const Avatar = ({ name = '', avatarUrl, size = 'md' }) => {
  const sizes = { xs: 'w-6 h-6 text-[10px]', sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-11 h-11 text-sm' };
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (avatarUrl) {
    const src = avatarUrl.startsWith('http') ? avatarUrl : `${BACKEND_URL}${avatarUrl}`;
    return <img src={src} alt={name} className={`${sizes[size]} rounded-xl object-cover border border-white/10 flex-shrink-0`} />;
  }
  return (
    <div className={`${sizes[size]} rounded-xl bg-gradient-to-br ${gradientFor(name)} flex items-center justify-center font-bold text-white flex-shrink-0 shadow-lg`}>
      {initial}
    </div>
  );
};

const timeAgo = (dateString) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const getRelativeDate = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return formatDate(d);
};

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════
const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest | popular | discussed
  const [searchQuery, setSearchQuery] = useState('');
  const [failedImages, setFailedImages] = useState({});
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const [likes, setLikes] = useState({});
  const [comments, setComments] = useState({});
  const [commentSort, setCommentSort] = useState('newest'); // newest | oldest | top
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [bookmarks, setBookmarks] = useState({}); // { [articleId]: bool }
  const [liveReaders, setLiveReaders] = useState({}); // { [articleId]: count }

  const userToken = typeof window !== 'undefined' ? localStorage.getItem('user_token') : null;
  const staffToken = typeof window !== 'undefined' ? localStorage.getItem('staff_token') : null;
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const token = userToken || staffToken || adminToken;
  const userData = (() => {
    try {
      return JSON.parse(
        localStorage.getItem('user_data') || localStorage.getItem('admin_user') || localStorage.getItem('staff_user') || 'null'
      );
    } catch { return null; }
  })();
  const isLoggedIn = !!token;
  const isStaffOrAdmin = !!staffToken || !!adminToken;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const handleImageError = (id) => setFailedImages(prev => ({ ...prev, [id]: true }));

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/news`);
      setArticles(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchLikes = useCallback(async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/news/${id}/likes`, { headers: authHeaders });
      setLikes(prev => ({ ...prev, [id]: res.data }));
    } catch {}
  }, [token]);

  const fetchComments = useCallback(async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/news/${id}/comments`, { headers: authHeaders });
      setComments(prev => ({ ...prev, [id]: res.data }));
    } catch {}
  }, [token]);

  useEffect(() => {
    if (selectedArticle) {
      fetchLikes(selectedArticle.id);
      fetchComments(selectedArticle.id);
      fetchBookmarkState(selectedArticle.id);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [selectedArticle, fetchLikes, fetchComments]);

  // Live-reader heartbeat + polling while article is open
  useEffect(() => {
    if (!selectedArticle) return;
    const articleId = selectedArticle.id;
    const beat = async () => {
      try {
        const r = await axios.post(`${BACKEND_URL}/api/news/${articleId}/heartbeat`, {}, { headers: authHeaders });
        setLiveReaders(prev => ({ ...prev, [articleId]: r.data.live_readers }));
      } catch {}
    };
    beat();
    const iv = setInterval(beat, 25000);
    return () => clearInterval(iv);
  }, [selectedArticle, token]);

  const fetchBookmarkState = async (id) => {
    if (!isLoggedIn) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/news/${id}/bookmark`, { headers: authHeaders });
      setBookmarks(prev => ({ ...prev, [id]: !!r.data.bookmarked }));
    } catch {}
  };

  const toggleBookmark = async (id, e) => {
    if (e) e.stopPropagation();
    if (!isLoggedIn) return setShowLoginPrompt(true);
    // Optimistic
    const was = !!bookmarks[id];
    setBookmarks(prev => ({ ...prev, [id]: !was }));
    try {
      const r = await axios.post(`${BACKEND_URL}/api/news/${id}/bookmark`, {}, { headers: authHeaders });
      setBookmarks(prev => ({ ...prev, [id]: !!r.data.bookmarked }));
      toast.success(r.data.bookmarked ? 'Saved to your reading list' : 'Removed from reading list');
    } catch {
      setBookmarks(prev => ({ ...prev, [id]: was }));
      toast.error('Could not update bookmark');
    }
  };

  // Reading progress bar
  useEffect(() => {
    if (!selectedArticle) return;
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [selectedArticle]);

  const toggleLike = async (id, e) => {
    if (e) e.stopPropagation();
    if (!isLoggedIn) return setShowLoginPrompt(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/news/${id}/like`, {}, { headers: authHeaders });
      setLikes(prev => ({ ...prev, [id]: { liked: res.data.liked, like_count: res.data.like_count } }));
      setArticles(prev => prev.map(a => a.id === id ? { ...a, like_count: res.data.like_count } : a));
    } catch { toast.error('Could not save your like'); }
  };

  const toggleCommentLike = async (articleId, commentId) => {
    if (!isLoggedIn) return setShowLoginPrompt(true);
    // Optimistic
    setComments(prev => ({
      ...prev,
      [articleId]: (prev[articleId] || []).map(c => c.id === commentId
        ? { ...c, liked_by_me: !c.liked_by_me, like_count: (c.like_count || 0) + (c.liked_by_me ? -1 : 1) }
        : c),
    }));
    try {
      const res = await axios.post(`${BACKEND_URL}/api/news/comments/${commentId}/like`, {}, { headers: authHeaders });
      setComments(prev => ({
        ...prev,
        [articleId]: (prev[articleId] || []).map(c => c.id === commentId
          ? { ...c, liked_by_me: res.data.liked, like_count: res.data.like_count } : c),
      }));
    } catch {
      // revert
      setComments(prev => ({
        ...prev,
        [articleId]: (prev[articleId] || []).map(c => c.id === commentId
          ? { ...c, liked_by_me: !c.liked_by_me, like_count: (c.like_count || 0) + (c.liked_by_me ? 1 : -1) } : c),
      }));
      toast.error('Could not save your reaction');
    }
  };

  const submitComment = async (id) => {
    if (!isLoggedIn) return setShowLoginPrompt(true);
    if (!newComment.trim()) return;
    if (newComment.length > 2000) return toast.error('Comment too long (max 2000 chars)');
    setSubmittingComment(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/news/${id}/comments`, { content: newComment.trim() }, { headers: authHeaders });
      setComments(prev => ({ ...prev, [id]: [...(prev[id] || []), res.data] }));
      setArticles(prev => prev.map(a => a.id === id ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a));
      setNewComment('');
      toast.success('Comment posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    } finally { setSubmittingComment(false); }
  };

  const submitReply = async (articleId, parentId) => {
    if (!isLoggedIn) return setShowLoginPrompt(true);
    if (!replyContent.trim()) return;
    setSubmittingReply(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/news/${articleId}/comments`,
        { content: replyContent.trim(), parent_id: parentId },
        { headers: authHeaders });
      setComments(prev => ({ ...prev, [articleId]: [...(prev[articleId] || []), res.data] }));
      setArticles(prev => prev.map(a => a.id === articleId ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a));
      setReplyContent('');
      setReplyingTo(null);
      toast.success('Reply posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally { setSubmittingReply(false); }
  };

  const deleteComment = async (articleId, commentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/news/${articleId}/comments/${commentId}`, { headers: authHeaders });
      setComments(prev => ({ ...prev, [articleId]: (prev[articleId] || []).filter(c => c.id !== commentId) }));
      setArticles(prev => prev.map(a => a.id === articleId ? { ...a, comment_count: Math.max(0, (a.comment_count || 1) - 1) } : a));
      toast.success('Comment removed');
    } catch { toast.error('Could not remove comment'); }
  };

  const shareArticle = async (article) => {
    const url = `${window.location.origin}/news?article=${article.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch {}
  };

  // Bulk-fetch bookmark state for visible articles (only when logged in)
  useEffect(() => {
    if (!isLoggedIn || articles.length === 0) return;
    (async () => {
      const ids = articles.map(a => a.id).slice(0, 25);
      const results = await Promise.all(ids.map(id =>
        axios.get(`${BACKEND_URL}/api/news/${id}/bookmark`, { headers: authHeaders })
          .then(r => [id, !!r.data.bookmarked]).catch(() => [id, false])
      ));
      const map = {};
      results.forEach(([id, b]) => { map[id] = b; });
      setBookmarks(prev => ({ ...map, ...prev }));
    })();
  }, [articles, token]);

  // Poll live readers for the featured article every 30s so the newsroom feels alive
  useEffect(() => {
    if (articles.length === 0 || selectedArticle) return;
    const featuredId = articles[0]?.id;
    if (!featuredId) return;
    const load = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/news/${featuredId}/live-readers`);
        setLiveReaders(prev => ({ ...prev, [featuredId]: r.data.live_readers }));
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [articles, selectedArticle]);

  // Categories + counts
  const categoryCounts = useMemo(() => {
    const map = { all: articles.length };
    articles.forEach(a => { if (a.category) map[a.category] = (map[a.category] || 0) + 1; });
    return map;
  }, [articles]);

  const filteredArticles = useMemo(() => {
    let list = articles.filter(a => {
      const matchesCat = filter === 'all' || a.category === filter;
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch = !q || a.title?.toLowerCase().includes(q) || a.content?.toLowerCase().includes(q);
      return matchesCat && matchesSearch;
    });
    if (sortBy === 'popular') list = [...list].sort((a, b) => (b.like_count || 0) - (a.like_count || 0));
    else if (sortBy === 'discussed') list = [...list].sort((a, b) => (b.comment_count || 0) - (a.comment_count || 0));
    else list = [...list].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
    return list;
  }, [articles, filter, searchQuery, sortBy]);

  const trending = useMemo(() => {
    return [...articles]
      .sort((a, b) => ((b.like_count || 0) + (b.comment_count || 0) * 2) - ((a.like_count || 0) + (a.comment_count || 0) * 2))
      .slice(0, 4);
  }, [articles]);

  // ═══════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════
  if (selectedArticle) {
    const articleId = selectedArticle.id;
    const articleComments = comments[articleId] || [];
    const currentUsername = userData?.username;
    const l = likes[articleId] || { liked: false, like_count: selectedArticle.like_count || 0 };
    const isBookmarked = !!bookmarks[articleId];
    const readerCount = liveReaders[articleId] || 0;

    // Build thread map: parent → children (single-level nesting)
    const topLevel = articleComments.filter(c => !c.parent_id);
    const repliesByParent = articleComments.reduce((acc, c) => {
      if (c.parent_id) { (acc[c.parent_id] ||= []).push(c); }
      return acc;
    }, {});

    const sortedTop = [...topLevel].sort((a, b) => {
      if (commentSort === 'top') return (b.like_count || 0) - (a.like_count || 0);
      if (commentSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return (
      <div className="min-h-screen bg-[#05050A]" data-testid="article-detail-view">
        {/* Reading progress bar */}
        <div className="fixed top-0 left-0 right-0 z-40 h-0.5 bg-transparent pointer-events-none">
          <div className="h-full bg-gradient-to-r from-[#00E5FF] via-[#8A2BE2] to-fuchsia-500 transition-all duration-100"
            style={{ width: `${readProgress}%` }} data-testid="read-progress" />
        </div>

        <article className="max-w-3xl mx-auto px-6 lg:px-8 pt-28 pb-24">
          <button onClick={() => { setSelectedArticle(null); setNewComment(''); }} data-testid="back-to-news-btn"
            className="inline-flex items-center gap-2 text-white/40 hover:text-[#00E5FF] text-xs font-medium mb-10 transition-colors group">
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" /> Back to News
          </button>

          {/* Article header */}
          <header className="mb-10" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {selectedArticle.category && (
                <Badge className="bg-gradient-to-r from-[#00E5FF]/20 to-[#8A2BE2]/20 text-[#00E5FF] border-[#00E5FF]/30 text-[10px] uppercase tracking-wider">
                  {selectedArticle.category}
                </Badge>
              )}
              <span className="text-white/25 text-xs flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />{formatDate(selectedArticle.published_at)}
              </span>
              <span className="text-white/15">·</span>
              <span className="text-white/25 text-xs flex items-center gap-1.5">
                <Clock className="w-3 h-3" />{selectedArticle.read_time || 3} min read
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-light tracking-tight text-white leading-[1.1] mb-8">
              {selectedArticle.title}
            </h1>
            {selectedArticle.excerpt && (
              <p className="text-lg text-white/50 leading-relaxed font-light border-l-2 border-[#00E5FF]/30 pl-5 mb-8">
                {selectedArticle.excerpt}
              </p>
            )}
            <div className="flex items-center justify-between pb-8 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <UserHoverCard username={selectedArticle.author_username || selectedArticle.author_name}>
                  <div className="cursor-pointer"><Avatar name={selectedArticle.author_name} size="lg" /></div>
                </UserHoverCard>
                <div>
                  <UserHoverCard username={selectedArticle.author_username || selectedArticle.author_name}>
                    <p className="text-white text-sm font-medium cursor-pointer hover:text-[#00E5FF] transition-colors">{selectedArticle.author_name}</p>
                  </UserHoverCard>
                  <p className="text-white/30 text-xs capitalize">{selectedArticle.author_role || 'Executive'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {readerCount > 0 && (
                  <div data-testid="live-readers-chip"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500/15 to-orange-500/10 border border-amber-500/25 text-amber-300 text-[11px]">
                    <span className="text-sm leading-none">🔥</span>
                    <span className="font-semibold">{readerCount}</span>
                    <span className="opacity-70">reading now</span>
                  </div>
                )}
                <button onClick={() => toggleBookmark(articleId)} data-testid="bookmark-btn"
                  title={isBookmarked ? 'Remove bookmark' : 'Save to reading list'}
                  className={`p-2.5 rounded-xl transition-all ${
                    isBookmarked ? 'bg-[#00E5FF]/15 text-[#00E5FF]' : 'hover:bg-white/[0.05] text-white/40 hover:text-white'
                  }`}>
                  {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                </button>
                <button onClick={() => shareArticle(selectedArticle)} data-testid="share-article-btn"
                  className="p-2.5 rounded-xl hover:bg-white/[0.05] text-white/40 hover:text-white transition-all" title="Share">
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </header>

          {/* Featured image */}
          {selectedArticle.image_url && !failedImages[`detail-${articleId}`] && (
            <figure className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-12 border border-white/[0.06]"
              style={{ animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
              <img src={getImageUrl(selectedArticle.image_url)} alt={selectedArticle.title}
                className="w-full h-full object-cover"
                onError={() => handleImageError(`detail-${articleId}`)} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050A]/40 to-transparent" />
            </figure>
          )}

          {/* Content */}
          <div className="prose prose-invert max-w-none" style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
            <div className="text-white/70 leading-[1.9] text-[17px] whitespace-pre-wrap font-light"
              dangerouslySetInnerHTML={{ __html: selectedArticle.content.replace(/\n/g, '<br/>') }} />
          </div>

          {/* Engagement bar */}
          <div className="flex items-center justify-between gap-6 mt-14 pt-6 border-t border-white/[0.06]" data-testid="article-engagement-bar">
            <div className="flex items-center gap-2">
              <button onClick={() => toggleLike(articleId)} data-testid={`like-btn-${articleId}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                  l.liked
                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                    : 'bg-white/[0.03] text-white/50 border-white/[0.08] hover:bg-white/[0.06] hover:border-white/15'
                }`}>
                <Heart className={`w-4 h-4 ${l.liked ? 'fill-red-400' : ''}`} />
                <span className="text-sm font-medium">{l.like_count}</span>
              </button>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white/50">
                <MessageSquare className="w-4 h-4" />
                <span className="text-sm font-medium">{articleComments.length}</span>
              </div>
            </div>
            <Button onClick={() => shareArticle(selectedArticle)} variant="ghost"
              className="text-white/40 hover:text-white hover:bg-white/[0.05] rounded-xl text-xs">
              <Link2 className="w-3.5 h-3.5 mr-2" /> Copy link
            </Button>
          </div>

          {/* ═══ COMMENTS SECTION ═══ */}
          <section className="mt-14" data-testid="comments-section">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <h3 className="text-xl font-light text-white flex items-center gap-2.5">
                <MessageSquare className="w-5 h-5 text-[#00E5FF]" />
                Comments <span className="text-white/30 text-sm ml-1">({articleComments.length})</span>
              </h3>
              {articleComments.length > 1 && (
                <div className="inline-flex items-center gap-1 bg-white/[0.03] border border-white/[0.06] rounded-xl p-1" data-testid="comment-sort">
                  {[
                    { id: 'newest', label: 'Newest' },
                    { id: 'oldest', label: 'Oldest' },
                    { id: 'top',    label: 'Top' },
                  ].map(o => (
                    <button key={o.id} onClick={() => setCommentSort(o.id)} data-testid={`sort-${o.id}`}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                        commentSort === o.id ? 'bg-white/[0.08] text-white' : 'text-white/40 hover:text-white/70'
                      }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Composer */}
            {isLoggedIn ? (
              <div className="mb-8 p-4 rounded-2xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08]" data-testid="comment-input-area">
                <div className="flex gap-3">
                  <Avatar name={userData?.first_name || userData?.display_name || userData?.username} avatarUrl={userData?.avatar_url} size="md" />
                  <div className="flex-1">
                    <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} data-testid="comment-textarea"
                      placeholder="Add to the conversation…"
                      rows={3} maxLength={2000}
                      className="w-full bg-transparent text-white placeholder-white/25 text-sm focus:outline-none resize-none leading-relaxed"
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submitComment(articleId); }
                      }} />
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05]">
                      <span className={`text-[10px] tracking-wider ${newComment.length > 1800 ? 'text-amber-400' : 'text-white/25'}`}>
                        {newComment.length}/2000 · Cmd/Ctrl + Enter to post
                      </span>
                      <div className="flex gap-2">
                        {newComment && (
                          <Button onClick={() => setNewComment('')} variant="ghost"
                            className="text-white/40 hover:text-white text-xs rounded-lg h-8">
                            Discard
                          </Button>
                        )}
                        <Button onClick={() => submitComment(articleId)} disabled={!newComment.trim() || submittingComment}
                          data-testid="submit-comment-btn"
                          className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 disabled:opacity-30 text-xs rounded-lg h-8 px-4">
                          <Send className="w-3 h-3 mr-1.5" />{submittingComment ? 'Posting…' : 'Post'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8 p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] text-center" data-testid="login-to-comment-prompt">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <p className="text-white/70 text-sm font-medium mb-1">Join the conversation</p>
                <p className="text-white/35 text-xs mb-4">Sign in to share your thoughts and react to comments</p>
                <div className="inline-flex gap-2">
                  <Button onClick={() => setShowLoginPrompt(true)} className="bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 hover:bg-[#00E5FF]/25 text-xs rounded-lg">
                    <LogIn className="w-3.5 h-3.5 mr-1.5" />Sign In
                  </Button>
                </div>
              </div>
            )}

            {/* Comments list */}
            {sortedTop.length === 0 ? (
              <div className="text-center py-14 rounded-2xl border border-dashed border-white/[0.06] bg-white/[0.01]">
                <Sparkles className="w-8 h-8 mx-auto mb-3 text-white/15" />
                <p className="text-white/40 text-sm">Be the first to comment</p>
                <p className="text-white/20 text-xs mt-1">Set the tone for the conversation</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedTop.map((c) => (
                  <CommentThread
                    key={c.id}
                    comment={c}
                    replies={repliesByParent[c.id] || []}
                    currentUsername={currentUsername}
                    isStaffOrAdmin={isStaffOrAdmin}
                    isLoggedIn={isLoggedIn}
                    userData={userData}
                    onLike={(cid) => toggleCommentLike(articleId, cid)}
                    onDelete={(cid) => deleteComment(articleId, cid)}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyContent={replyContent}
                    setReplyContent={setReplyContent}
                    onSubmitReply={() => submitReply(articleId, c.id)}
                    submitting={submittingReply}
                    onNeedLogin={() => setShowLoginPrompt(true)}
                  />
                ))}
              </div>
            )}
          </section>
        </article>

        <LoginDialog open={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />
        <PageStyles />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════
  const featured = filteredArticles[0];
  const rest = filteredArticles.slice(1);

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="news-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#00E5FF]/[0.04] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[#8A2BE2]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00E5FF]/20 bg-[#00E5FF]/5 backdrop-blur-md mb-6"
                style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
                <Radio className="w-3 h-3 text-[#00E5FF]" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#00E5FF] font-semibold">The Newsroom · Live</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white mb-3"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>
                News &{' '}
                <span className="italic bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent">Announcements</span>
              </h1>
              <p className="text-base md:text-lg text-white/40 max-w-xl leading-relaxed font-light"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both' }}>
                Latest updates from Starnight Holdings and our managed groups.
              </p>
            </div>
            {/* Live counter */}
            <div className="hidden md:flex items-center gap-4 text-white/40 text-xs" style={{ animation: 'fadeInUp 0.6s ease-out 0.3s both' }}>
              <div className="text-right">
                <p className="text-white text-2xl font-light">{articles.length}</p>
                <p className="text-white/30 text-[10px] uppercase tracking-wider">Stories</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-right">
                <p className="text-white text-2xl font-light">{articles.reduce((s, a) => s + (a.like_count || 0), 0)}</p>
                <p className="text-white/30 text-[10px] uppercase tracking-wider">Reactions</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-right">
                <p className="text-white text-2xl font-light">{articles.reduce((s, a) => s + (a.comment_count || 0), 0)}</p>
                <p className="text-white/30 text-[10px] uppercase tracking-wider">Comments</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar: search + categories + sort */}
      <section className="sticky top-16 z-20 backdrop-blur-xl bg-[#05050A]/80 border-y border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-3">
          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search stories, topics, authors…" data-testid="news-search"
                className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:border-[#00E5FF]/40 focus:ring-1 focus:ring-[#00E5FF]/20 focus:outline-none text-sm" />
            </div>

            {/* Category chips */}
            <div className="flex gap-1.5 flex-wrap">
              {Object.keys(categoryCounts).map(cat => (
                <button key={cat} onClick={() => setFilter(cat)} data-testid={`news-filter-${cat}`}
                  className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap inline-flex items-center gap-1.5 ${
                    filter === cat
                      ? 'bg-gradient-to-r from-[#00E5FF]/15 to-[#8A2BE2]/10 text-white border border-[#00E5FF]/30'
                      : 'bg-white/[0.02] text-white/40 border border-white/[0.06] hover:text-white hover:border-white/15'
                  }`}>
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  <span className={`text-[10px] ${filter === cat ? 'text-[#00E5FF]' : 'text-white/30'}`}>
                    {categoryCounts[cat]}
                  </span>
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="inline-flex items-center gap-1 bg-white/[0.03] border border-white/[0.08] rounded-xl p-1" data-testid="news-sort">
              {[
                { id: 'newest',    label: 'Newest',    Icon: Clock },
                { id: 'popular',   label: 'Popular',   Icon: Flame },
                { id: 'discussed', label: 'Discussed', Icon: MessageSquare },
              ].map(o => (
                <button key={o.id} onClick={() => setSortBy(o.id)} data-testid={`sort-${o.id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all inline-flex items-center gap-1.5 ${
                    sortBy === o.id ? 'bg-white/[0.08] text-white' : 'text-white/40 hover:text-white/70'
                  }`}>
                  <o.Icon className="w-3 h-3" /> <span className="hidden sm:inline">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#00E5FF] animate-spin" />
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-24 rounded-2xl border border-dashed border-white/[0.06] bg-white/[0.01]">
              <Newspaper className="w-16 h-16 mx-auto mb-4 text-white/10" />
              <h3 className="text-lg font-medium text-white mb-2">{articles.length === 0 ? 'No stories yet' : 'Nothing to show'}</h3>
              <p className="text-white/30 text-sm max-w-sm mx-auto">
                {articles.length === 0 ? 'Executives will publish updates here soon.' : 'Try clearing filters or a different keyword.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-8">
              {/* Main feed */}
              <div className="min-w-0 space-y-5">
                {/* Featured */}
                {featured && (
                  <FeaturedArticle article={featured} onClick={() => setSelectedArticle(featured)}
                    imgFailed={failedImages[featured.id]} onImageError={() => handleImageError(featured.id)}
                    isBookmarked={!!bookmarks[featured.id]}
                    onBookmark={(e) => { e?.stopPropagation?.(); toggleBookmark(featured.id, e); }} />
                )}

                {/* Section divider */}
                {rest.length > 0 && (
                  <div className="flex items-center gap-3 pt-4">
                    <div className="h-px flex-1 bg-white/[0.06]" />
                    <span className="text-white/30 text-[10px] uppercase tracking-[0.25em] font-medium">Latest stories</span>
                    <div className="h-px flex-1 bg-white/[0.06]" />
                  </div>
                )}

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {rest.map((article, index) => (
                    <ArticleCard key={article.id} article={article} index={index}
                      onClick={() => setSelectedArticle(article)}
                      imgFailed={failedImages[article.id]}
                      onImageError={() => handleImageError(article.id)}
                      isBookmarked={!!bookmarks[article.id]}
                      onBookmark={(e) => { e?.stopPropagation?.(); toggleBookmark(article.id, e); }} />
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:sticky lg:top-32 lg:self-start space-y-5">
                {/* Trending */}
                <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.02] to-transparent p-5"
                  data-testid="trending-section">
                  <h3 className="text-white font-medium flex items-center gap-2 text-sm mb-4">
                    <Flame className="w-4 h-4 text-amber-400" /> Trending now
                  </h3>
                  {trending.length === 0 ? (
                    <p className="text-white/30 text-xs">Nothing trending yet</p>
                  ) : (
                    <div className="space-y-3">
                      {trending.map((a, i) => (
                        <button key={a.id} onClick={() => setSelectedArticle(a)} data-testid={`trending-${a.id}`}
                          className="w-full text-left group flex gap-3 items-start hover:bg-white/[0.03] -m-1 p-1 rounded-lg transition-all">
                          <span className="text-white/20 text-2xl font-light leading-none flex-shrink-0 w-5 group-hover:text-[#00E5FF] transition-colors">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-white/80 text-xs font-medium line-clamp-2 group-hover:text-white leading-snug">{a.title}</p>
                            <div className="flex items-center gap-2 text-white/25 text-[10px] mt-1.5">
                              <span className="inline-flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" /> {a.like_count || 0}</span>
                              <span>·</span>
                              <span className="inline-flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" /> {a.comment_count || 0}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Subscribe CTA */}
                <div className="rounded-2xl border border-[#00E5FF]/15 bg-gradient-to-br from-[#00E5FF]/[0.08] to-[#8A2BE2]/[0.05] p-5 relative overflow-hidden">
                  <div className="absolute -right-6 -top-6 w-24 h-24 bg-[#00E5FF]/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <Sparkles className="w-5 h-5 text-[#00E5FF] mb-3" />
                    <h3 className="text-white font-medium text-sm mb-1">Never miss a story</h3>
                    <p className="text-white/40 text-xs mb-4 leading-relaxed">Sign up for a Starnight account to like posts and jump into the comments.</p>
                    {isLoggedIn ? (
                      <Button onClick={() => window.location.href = '/account/settings?tab=inbox'}
                        className="w-full bg-white/10 hover:bg-white/15 text-white border border-white/15 text-xs rounded-lg h-9">
                        Your Inbox <ArrowUpRight className="w-3 h-3 ml-1" />
                      </Button>
                    ) : (
                      <Button onClick={() => window.location.href = '/signup'} data-testid="sidebar-signup-btn"
                        className="w-full bg-white text-black hover:bg-white/90 text-xs rounded-lg h-9">
                        Create free account
                      </Button>
                    )}
                  </div>
                </div>

                {/* Categories */}
                {Object.keys(categoryCounts).length > 2 && (
                  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                    <h3 className="text-white/60 text-[10px] uppercase tracking-[0.2em] font-medium mb-3">Categories</h3>
                    <div className="space-y-1">
                      {Object.entries(categoryCounts).filter(([k]) => k !== 'all').map(([k, v]) => (
                        <button key={k} onClick={() => setFilter(k)}
                          className={`w-full flex items-center justify-between text-xs px-2 py-1.5 rounded-lg transition-all ${
                            filter === k ? 'bg-white/[0.06] text-white' : 'text-white/50 hover:text-white hover:bg-white/[0.03]'
                          }`}>
                          <span>{k}</span>
                          <span className="text-white/25">{v}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          )}
        </div>
      </section>

      <LoginDialog open={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />
      <PageStyles />
    </div>
  );
};

// ═══ Comment / Thread renderer ═══
const CommentRow = ({ c, currentUsername, isStaffOrAdmin, onLike, onDelete, onReply, canReply, isReply = false }) => {
  const isOwn = c.username === currentUsername;
  const highlight = c.tags?.includes('Executive') || c.tags?.includes('Staff') || c.tags?.includes('Moderator');
  return (
    <div data-testid={`comment-${c.id}`}
      className={`group relative rounded-2xl border transition-all overflow-hidden ${
        highlight
          ? 'bg-gradient-to-br from-[#8A2BE2]/[0.06] to-[#00E5FF]/[0.03] border-[#00E5FF]/15'
          : 'bg-white/[0.02] border-white/[0.05] hover:border-white/[0.1]'
      }`}>
      {highlight && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00E5FF] to-[#8A2BE2]" />}
      <div className="flex gap-3 p-4">
        <UserHoverCard username={c.username}>
          <div className="cursor-pointer"><Avatar name={c.display_name} avatarUrl={c.avatar_url} size="md" /></div>
        </UserHoverCard>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <UserHoverCard username={c.username}>
              <span className="text-white text-sm font-semibold cursor-pointer hover:text-[#00E5FF] transition-colors">
                {c.display_name}
              </span>
            </UserHoverCard>
            {c.tags?.slice(0, 3).map(tag => <UserTag key={tag} tag={tag} />)}
            {isOwn && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25">You</span>
            )}
            <span className="text-white/25 text-[11px]" title={new Date(c.created_at).toLocaleString('en-GB')}>· {timeAgo(c.created_at)}</span>
            {c.edited && <span className="text-white/25 text-[10px] italic">edited</span>}
          </div>
          <p className="text-white/75 text-sm leading-relaxed whitespace-pre-wrap break-words">{c.content}</p>
          <div className="flex items-center gap-1 mt-3 -ml-2">
            <button onClick={() => onLike(c.id)} data-testid={`comment-like-${c.id}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-all ${
                c.liked_by_me ? 'bg-red-500/10 text-red-400' : 'text-white/40 hover:text-red-400 hover:bg-white/[0.04]'
              }`}>
              <Heart className={`w-3.5 h-3.5 ${c.liked_by_me ? 'fill-red-400' : ''}`} />
              <span className="font-medium">{c.like_count || 0}</span>
            </button>
            {canReply && !isReply && (
              <button onClick={() => onReply(c.id)} data-testid={`reply-btn-${c.id}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-white/40 hover:text-[#00E5FF] hover:bg-white/[0.04] transition-all">
                <Reply className="w-3.5 h-3.5" /> Reply
              </button>
            )}
            {(isOwn || isStaffOrAdmin) && (
              <button onClick={() => onDelete(c.id)} data-testid={`delete-comment-${c.id}`}
                className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-white/30 hover:text-red-400 hover:bg-white/[0.04] transition-all"
                title={isOwn ? 'Delete your comment' : 'Remove (moderation)'}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CommentThread = ({
  comment: c, replies = [], currentUsername, isStaffOrAdmin, isLoggedIn, userData,
  onLike, onDelete, replyingTo, setReplyingTo, replyContent, setReplyContent, onSubmitReply, submitting, onNeedLogin,
}) => {
  const isReplyingHere = replyingTo === c.id;
  return (
    <div>
      <CommentRow c={c} currentUsername={currentUsername} isStaffOrAdmin={isStaffOrAdmin}
        onLike={onLike} onDelete={onDelete}
        onReply={(cid) => { if (!isLoggedIn) return onNeedLogin(); setReplyingTo(cid); setReplyContent(''); }}
        canReply />

      {/* Reply composer (inline) */}
      {isReplyingHere && (
        <div className="mt-2 ml-6 pl-4 border-l-2 border-[#00E5FF]/20" data-testid={`reply-composer-${c.id}`}>
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.08] p-3">
            <div className="flex gap-2.5 items-start">
              <Avatar name={userData?.first_name || userData?.username} avatarUrl={userData?.avatar_url} size="sm" />
              <div className="flex-1">
                <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} data-testid={`reply-textarea-${c.id}`}
                  autoFocus rows={2} maxLength={2000}
                  placeholder={`Reply to ${c.display_name}…`}
                  className="w-full bg-transparent text-white placeholder-white/25 text-sm focus:outline-none resize-none leading-relaxed"
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSubmitReply(); }
                  }} />
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.05]">
                  <span className="text-white/25 text-[10px]">{replyContent.length}/2000</span>
                  <div className="flex gap-1.5">
                    <Button onClick={() => setReplyingTo(null)} variant="ghost"
                      className="text-white/40 hover:text-white text-xs rounded-lg h-7 px-3">Cancel</Button>
                    <Button onClick={onSubmitReply} disabled={!replyContent.trim() || submitting}
                      data-testid={`submit-reply-${c.id}`}
                      className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] text-black hover:opacity-90 disabled:opacity-30 text-xs rounded-lg h-7 px-3">
                      <Send className="w-3 h-3 mr-1" />{submitting ? '…' : 'Reply'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Replies (nested one level) */}
      {replies.length > 0 && (
        <div className="mt-2 ml-6 pl-4 border-l-2 border-white/[0.06] space-y-2">
          {[...replies].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((r) => (
            <div key={r.id} className="relative">
              <CornerDownRight className="absolute -left-6 top-4 w-3 h-3 text-white/15" />
              <CommentRow c={r} currentUsername={currentUsername} isStaffOrAdmin={isStaffOrAdmin}
                onLike={onLike} onDelete={onDelete} onReply={() => {}} canReply={false} isReply />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══ Featured Article Card ═══
const FeaturedArticle = ({ article, onClick, imgFailed, onImageError, isBookmarked, onBookmark }) => (
  <div data-testid={`featured-article-${article.id}`} onClick={onClick}
    className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.03] cursor-pointer transition-all duration-300 overflow-hidden hover:border-[#00E5FF]/20 hover:-translate-y-0.5"
    style={{ animation: 'fadeInUp 0.5s ease-out' }}>
    <div className="grid md:grid-cols-[1.15fr,1fr] gap-0">
      <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[320px] overflow-hidden">
        {article.image_url && !imgFailed ? (
          <img src={getImageUrl(article.image_url)} alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            onError={onImageError} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0D0D14] via-[#0A0A12] to-[#05050A] flex items-center justify-center">
            <Newspaper className="w-16 h-16 text-white/5" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#05050A]/60 hidden md:block" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent md:hidden" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black border-none text-[10px] font-bold uppercase tracking-wider">
            <Sparkles className="w-2.5 h-2.5 mr-1" /> Featured
          </Badge>
          {article.category && (
            <Badge className="bg-black/60 backdrop-blur-sm text-white/80 border-white/10 text-[10px]">{article.category}</Badge>
          )}
        </div>
        <button onClick={onBookmark} data-testid={`bookmark-${article.id}`}
          title={isBookmarked ? 'Remove bookmark' : 'Save to reading list'}
          className={`absolute top-4 right-4 p-2 rounded-xl backdrop-blur-md border transition-all ${
            isBookmarked ? 'bg-[#00E5FF]/20 border-[#00E5FF]/40 text-[#00E5FF]' : 'bg-black/40 border-white/10 text-white/60 hover:text-white'
          }`}>
          {isBookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="p-6 md:p-8 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-4 text-white/30 text-[11px]">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {getRelativeDate(article.published_at)}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_time || 3} min read</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-4 group-hover:text-[#00E5FF] transition-colors leading-tight">
          {article.title}
        </h2>
        <p className="text-white/40 text-sm leading-relaxed line-clamp-3 mb-6 font-light">
          {article.excerpt || article.content?.substring(0, 180) + '…'}
        </p>
        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center gap-2.5">
            <Avatar name={article.author_name} size="sm" />
            <div className="min-w-0">
              <p className="text-white/70 text-xs font-medium truncate">{article.author_name}</p>
              <p className="text-white/25 text-[10px] capitalize">{article.author_role || 'Executive'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-white/30 text-xs">
            <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {article.like_count || 0}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {article.comment_count || 0}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ═══ Standard Article Card ═══
const ArticleCard = ({ article, index, onClick, imgFailed, onImageError, isBookmarked, onBookmark }) => (
  <div key={article.id} data-testid={`news-article-${article.id}`} onClick={onClick}
    className="group rounded-2xl border border-white/[0.05] bg-white/[0.015] hover:bg-white/[0.03] cursor-pointer transition-all duration-300 overflow-hidden hover:border-white/[0.12] hover:-translate-y-0.5"
    style={{ animation: `fadeInUp 0.4s ease-out ${0.05 * index}s both` }}>
    <div className="relative aspect-[16/10] overflow-hidden">
      {article.image_url && !imgFailed ? (
        <img src={getImageUrl(article.image_url)} alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={onImageError} />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#0D0D14] to-[#05050A] flex items-center justify-center">
          <Newspaper className="w-10 h-10 text-white/5" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/20 to-transparent" />
      {article.category && (
        <Badge className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white/90 border-white/10 text-[9px] uppercase tracking-wider">
          {article.category}
        </Badge>
      )}
      <button onClick={onBookmark} data-testid={`bookmark-${article.id}`}
        className={`absolute top-3 right-3 p-1.5 rounded-lg backdrop-blur-md border transition-all opacity-0 group-hover:opacity-100 ${
          isBookmarked ? 'bg-[#00E5FF]/20 border-[#00E5FF]/40 text-[#00E5FF] opacity-100' : 'bg-black/40 border-white/10 text-white/70 hover:text-white'
        }`}>
        {isBookmarked ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
      </button>
    </div>
    <div className="p-5">
      <div className="flex items-center gap-2 text-white/25 text-[10px] mb-3">
        <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {getRelativeDate(article.published_at)}</span>
        <span>·</span>
        <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {article.read_time || 3}m</span>
      </div>
      <h3 className="text-base font-medium text-white mb-2 group-hover:text-[#00E5FF] transition-colors line-clamp-2 tracking-tight leading-snug">
        {article.title}
      </h3>
      <p className="text-white/40 text-xs line-clamp-2 leading-relaxed mb-4 font-light">
        {article.excerpt || article.content?.substring(0, 100) + '…'}
      </p>
      <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar name={article.author_name} size="xs" />
          <span className="text-white/40 text-[11px] truncate">{article.author_name}</span>
        </div>
        <div className="flex items-center gap-2.5 text-white/25 text-[11px] flex-shrink-0">
          <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" /> {article.like_count || 0}</span>
          <span className="inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {article.comment_count || 0}</span>
        </div>
      </div>
    </div>
  </div>
);

// ═══ Login Prompt ═══
const LoginDialog = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="bg-[#0A0A12] border-white/10 text-white max-w-sm p-8" data-testid="login-prompt-dialog">
      <DialogTitle className="sr-only">Sign in required</DialogTitle>
      <div className="text-center">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-[#00E5FF]/20 to-[#8A2BE2]/20 border border-[#00E5FF]/25 flex items-center justify-center">
          <LogIn className="w-6 h-6 text-[#00E5FF]" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Sign in to interact</h3>
        <p className="text-white/40 text-sm mb-6">Create an account or sign in to like articles and join the conversation.</p>
        <div className="flex flex-col gap-2">
          <Button onClick={() => window.location.href = '/signup'} data-testid="prompt-signup-btn"
            className="w-full bg-white text-black hover:bg-white/90 font-medium">Create Account</Button>
          <Button onClick={() => window.location.href = '/login'} data-testid="prompt-login-btn"
            className="w-full bg-white/[0.05] text-white/70 border border-white/10 hover:bg-white/10">Sign In</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const PageStyles = () => (
  <style>{`
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `}</style>
);

export default News;
